import type { UserInfo } from "@/lib/authBackend/trimble/tid/client";

export const REGISTER_PUBLIC_PATH = "/register-public";

export const TRIAL_PREFILL_FIELDS = ["firstName", "lastName", "email", "phone", "company", "companyRole"] as const;

export type TrialPrefillField = (typeof TRIAL_PREFILL_FIELDS)[number];

export type TrialPrefill = Partial<Record<TrialPrefillField, string>>;

// These ride in the OAuth state, so a crafted link must not bloat the authorize URL.
const MAX_VALUE_LENGTH = 200;

function normalize(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, MAX_VALUE_LENGTH) : undefined;
}

export function readTrialPrefill(params: URLSearchParams | null | undefined): TrialPrefill {
  const prefill: TrialPrefill = {};
  if (!params) return prefill;
  for (const field of TRIAL_PREFILL_FIELDS) {
    const value = normalize(params.get(field));
    if (value) prefill[field] = value;
  }
  return prefill;
}

export function buildTrialPrefillQuery(prefill: TrialPrefill): string {
  const params = new URLSearchParams();
  for (const field of TRIAL_PREFILL_FIELDS) {
    const value = prefill[field];
    if (value) params.set(field, value);
  }
  return params.toString();
}

export const TRIAL_ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type TrialAttributionParam = (typeof TRIAL_ATTRIBUTION_PARAMS)[number];

export type TrialAttribution = Partial<Record<TrialAttributionParam, string>>;

export interface TrialAttributionPayload {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

export function readTrialAttribution(params: URLSearchParams | null | undefined): TrialAttribution {
  const attribution: TrialAttribution = {};
  if (!params) return attribution;
  for (const param of TRIAL_ATTRIBUTION_PARAMS) {
    const value = normalize(params.get(param));
    if (value) attribution[param] = value;
  }
  return attribution;
}

export const ATTRIBUTION_COOKIE = "dc_attribution";

export function readAttributionCookie(cookie: string | null | undefined): TrialAttribution {
  const attribution: TrialAttribution = {};
  const entry = cookie?.split("; ").find((row) => row.startsWith(`${ATTRIBUTION_COOKIE}=`));
  if (!entry) return attribution;

  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(entry.slice(ATTRIBUTION_COOKIE.length + 1)));
  } catch {
    return attribution;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return attribution;

  const record = parsed as Record<string, unknown>;
  for (const param of TRIAL_ATTRIBUTION_PARAMS) {
    const value = record[param];
    const normalized = typeof value === "string" ? normalize(value) : undefined;
    if (normalized) attribution[param] = normalized;
  }
  return attribution;
}

// The cookie and the URL describe different visits, so the winner takes every key —
// blending them would invent a source/campaign pair that never ran.
export function resolveTrialAttribution(params: URLSearchParams | null | undefined): TrialAttribution {
  const fromParams = readTrialAttribution(params);
  if (Object.keys(fromParams).length > 0) return fromParams;
  return readAttributionCookie(typeof document === "undefined" ? null : document.cookie);
}

export function buildTrialHandoffQuery(params: URLSearchParams | null | undefined): string {
  const query = new URLSearchParams(buildTrialPrefillQuery(readTrialPrefill(params)));
  const attribution = readTrialAttribution(params);
  for (const param of TRIAL_ATTRIBUTION_PARAMS) {
    const value = attribution[param];
    if (value) query.set(param, value);
  }
  return query.toString();
}

export function toTrialAttributionPayload(attribution: TrialAttribution): TrialAttributionPayload {
  return {
    utmSource: attribution.utm_source,
    utmMedium: attribution.utm_medium,
    utmCampaign: attribution.utm_campaign,
    utmContent: attribution.utm_content,
    utmTerm: attribution.utm_term,
  };
}

export interface TrialProvisioning {
  company: string;
  companyRole?: string;
  phone?: string;
}

// Provisioning needs company; everything else the visitor typed was already sent to
// HubSpot before the Trimble ID hop and never has to survive it.
export function readTrialProvisioning(params: URLSearchParams | null | undefined): TrialProvisioning | undefined {
  const company = normalize(params?.get("company"));
  if (!company) return undefined;
  const companyRole = normalize(params?.get("companyRole"));
  const phone = normalize(params?.get("phone"));
  return {
    company,
    ...(companyRole ? { companyRole } : {}),
    ...(phone ? { phone } : {}),
  };
}

export function buildSubmittedTrialHandoffQuery(
  params: URLSearchParams | null | undefined,
  values: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    company?: string;
    companyRole?: string;
  },
): string {
  const query = new URLSearchParams();
  const attribution = readTrialAttribution(params);
  for (const param of TRIAL_ATTRIBUTION_PARAMS) {
    const value = attribution[param];
    if (value) query.set(param, value);
  }
  for (const field of TRIAL_PREFILL_FIELDS) {
    const value = normalize(values[field]);
    if (value) query.set(field, value);
  }
  return query.toString();
}

export function buildRegisterPublicRedirect(query: string): string {
  return query ? `${REGISTER_PUBLIC_PATH}?${query}` : REGISTER_PUBLIC_PATH;
}

function claim(claims: UserInfo, name: string): string | undefined {
  const value = claims[name];
  return typeof value === "string" ? normalize(value) : undefined;
}

// TID has no company claim, so company never comes from here.
export function trialPrefillFromClaims(claims: UserInfo | null | undefined): TrialPrefill {
  if (!claims) return {};
  const prefill: TrialPrefill = {};
  const firstName = claim(claims, "given_name");
  const lastName = claim(claims, "family_name");
  const email = claim(claims, "email");
  if (firstName) prefill.firstName = firstName;
  if (lastName) prefill.lastName = lastName;
  if (email) prefill.email = email;
  return prefill;
}
